import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { QuickAddProduct } from "@/components/quick-add";
import { useAuth } from "@/lib/auth";
import { inr, fmtDate, statusColor } from "@/lib/utils-format";
import { Plus, Trash2, Eye, Truck, Wallet, Undo2, Loader2, Building2, Pencil, Ban, Upload, Download, FileSpreadsheet, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { ForceDeleteDialog } from "@/components/ForceDeleteDialog";
import { VpDispatchSection } from "@/components/VpDispatchSection";
const DISPATCH_ROLES = ["super_admin", "admin", "warehouse_manager"];

// ---- Bulk import of vendor purchase orders from Excel ----
function BulkImportDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const reset = () => { setFile(null); setResult(null); };
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/vendor-purchases/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "vendor_purchase_import_template.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };
  const upload = async (commit) => {
    if (!file) return toast.error("Choose an Excel file first");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/vendor-purchases/import?commit=${commit}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult({ ...data, committed_run: commit });
      if (commit) { toast.success(`Purchase order ${data.committed[0]?.number || ""} created with ${data.committed[0]?.lines || 0} line items`); onDone?.(); }
      else if (data.invalid) toast.warning(`${data.invalid} row${data.invalid === 1 ? "" : "s"} need fixing before import`);
      else toast.success(`All ${data.valid} line items look good`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="vp-bulk-import-btn" className="border-[#C05621] text-[#C05621] hover:bg-[#FDF3EC]"><Upload className="w-4 h-4 mr-1" /> Bulk Import</Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto" data-testid="vp-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#C05621]" /> Bulk import vendor purchase orders</DialogTitle>
          <DialogDescription>One Excel upload = one purchase order. The first data row gives the vendor, invoice ref and date; every row (including the first) is a line item. All rows must pass validation before the PO is saved.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-[#EAE5D9] bg-[#FDFBF7] p-3">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="vp-import-template-btn"><Download className="w-4 h-4 mr-1" /> Download sample template</Button>
          <span className="text-xs text-[#8B7355]">Includes a header row + sample line, a Reference sheet with your vendors & SKUs, and the fields Barcode + Label Ingredients.</span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} className="max-w-sm" data-testid="vp-import-file" />
          <Button onClick={() => upload(false)} disabled={!file || busy} data-testid="vp-import-validate-btn" className="bg-[#2C1810] text-white">{busy && !result?.committed_run ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Validate rows</Button>
        </div>
        {result && (
          <div className="space-y-3" data-testid="vp-import-result">
            <div className="flex flex-wrap gap-2 text-sm items-center">
              {result.header && <span className="text-xs text-[#5C4033] mr-2" data-testid="vp-import-header">PO for <b>{result.header.vendor}</b>{result.header.invoice_ref ? ` · Inv ${result.header.invoice_ref}` : ""}{result.header.purchase_date ? ` · ${result.header.purchase_date}` : ""}{result.header.is_interstate ? " · IGST" : ""}</span>}
              <Badge variant="outline" data-testid="vp-import-total">{result.total} line item{result.total === 1 ? "" : "s"}</Badge>
              <Badge className="bg-emerald-600 text-white" data-testid="vp-import-valid">{result.valid} valid</Badge>
              <Badge className={result.invalid ? "bg-red-600 text-white" : "bg-[#EAE5D9] text-[#5C4033]"} data-testid="vp-import-invalid">{result.invalid} with errors</Badge>
              {result.committed_run && result.committed[0] && <Badge className="bg-[#C05621] text-white" data-testid="vp-import-created">Created {result.committed[0].number} · {result.committed[0].lines} lines</Badge>}
            </div>
            {result.invalid > 0 && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3" data-testid="vp-import-errors">
                <div className="text-sm font-semibold text-red-800 mb-1">Fix these rows in the Excel and upload again — the PO is saved only when every line is valid</div>
                <ul className="text-xs text-red-800 space-y-1 max-h-48 overflow-y-auto">
                  {result.rows.filter((r) => !r.valid).map((r) => (
                    <li key={r.row} data-testid={`vp-import-error-row-${r.row}`}><b>Row {r.row}</b>{r.sku ? ` (${r.sku})` : ""}: {r.errors.join("; ")}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="border border-[#EAE5D9] rounded-md overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F7F2E8] uc-label sticky top-0"><tr><th className="px-2 py-1.5 text-left">Row</th><th className="px-2 py-1.5 text-left">Vendor</th><th className="px-2 py-1.5 text-left">SKU / Product</th><th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Price</th><th className="px-2 py-1.5">Mfg / Exp</th><th className="px-2 py-1.5">Barcode</th><th className="px-2 py-1.5">Status</th></tr></thead>
                <tbody>
                  {result.rows.map((r) => {
                    const made = result.committed_run && result.committed[0];
                    return (
                      <tr key={r.row} className={`border-t border-[#EAE5D9] ${r.valid ? "" : "bg-red-50/60"}`} data-testid={`vp-import-row-${r.row}`}>
                        <td className="px-2 py-1.5">{r.row}</td><td className="px-2 py-1.5">{r.vendor}</td><td className="px-2 py-1.5"><span className="font-mono">{r.sku}</span> {r.product ? `· ${r.product}` : ""}</td>
                        <td className="px-2 py-1.5 text-right">{r.quantity ?? "—"}</td><td className="px-2 py-1.5 text-right">{r.unit_price ?? "—"}</td><td className="px-2 py-1.5 text-center">{r.mfg_date || "—"} / {r.expiry_date || "—"}</td><td className="px-2 py-1.5 font-mono">{r.barcode || "—"}</td>
                        <td className="px-2 py-1.5">{made ? <span className="text-[#C05621] font-medium">{made.number}</span> : r.valid ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Valid</span> : <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="w-3.5 h-3.5" /> Error</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="vp-import-close">Close</Button>
          {result && !result.committed_run && (
            <Button onClick={() => upload(true)} disabled={busy || !result.can_commit} data-testid="vp-import-commit-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} {result.can_commit ? `Create PO with ${result.valid} line item${result.valid === 1 ? "" : "s"}` : "Fix errors to import"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyItem = { product_id: "", name: "", sku: "", quantity: 1, unit: "pcs", unit_price: 0, gst_rate: 5, mfg_date: "", expiry_date: "", ho_transfer_rate: "", barcode: "", label_ingredients: "" };
const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyPurchaseForm = { vendor_id: "", items: [{ ...emptyItem }], is_interstate: false, notes: "", invoice_ref: "", doc_date: todayStr() };
const emptyVendorForm = { name: "", gstin: "", phone: "", email: "", address: "" };

function ProductQuickPicker({ onPick, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (q.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products?search=${encodeURIComponent(q)}`);
        setSuggestions((data || []).slice(0, 6));
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product…" className="h-8 text-xs" data-testid={testid} />
      {suggestions.length > 0 && (
        <div className="absolute z-10 left-0 right-0 border border-[#EAE5D9] rounded mt-1 max-h-40 overflow-y-auto bg-white shadow-md">
          {suggestions.map((p) => (
            <button key={p.id} onClick={() => { onPick(p); setSuggestions([]); setQ(""); }}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#FDFBF7] border-b border-[#EAE5D9] last:border-0">
              <span className="font-medium">{p.name}</span> <span className="text-[#8B7355]">({p.sku})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VendorsTab({ vendors, load, isSuperAdmin }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyVendorForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const openEdit = (v) => { setEditingId(v.id); setForm({ name: v.name || "", gstin: v.gstin || "", phone: v.phone || "", email: v.email || "", address: v.address || "" }); setOpen(true); };
  const removeVendor = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? Blocked if it has purchase bills.`)) return;
    try { await api.delete(`/vendors/${v.id}`); toast.success("Vendor deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Vendor name required");
    setSaving(true);
    try {
      if (editingId) { await api.patch(`/vendors/${editingId}`, form); toast.success("Vendor updated"); }
      else { await api.post("/vendors", form); toast.success("Vendor added"); }
      setOpen(false); setForm(emptyVendorForm); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyVendorForm); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="vendor-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-white">
            <DialogHeader><DialogTitle className="font-[Outfit]">{editingId ? "Edit Vendor" : "New Vendor"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Vendor / Company Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" autoFocus /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} data-testid="vendor-gstin-input" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-phone-input" /></div>
              </div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-email-input" /></div>
              <div><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="vendor-address-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="vendor-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Vendor"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Vendor</th><th className="px-4 py-3">GSTIN</th>
              <th className="px-4 py-3">Phone</th><th className="px-4 py-3">Email</th>
              {isSuperAdmin && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {vendors.map((v) => (
              <tr key={v.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`vendor-row-${v.id}`}>
                <td className="px-4 py-3 font-medium flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-[#8B7355]" />{v.name}</td>
                <td className="px-4 py-3 text-[#5C4033]">{v.gstin || "—"}</td>
                <td className="px-4 py-3 text-[#5C4033]">{v.phone || "—"}</td>
                <td className="px-4 py-3 text-[#5C4033]">{v.email || "—"}</td>
                {isSuperAdmin && (
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(v)} data-testid={`vendor-edit-${v.id}`} title="Edit (Super Admin)" className="p-1.5 hover:bg-blue-50 text-blue-600 rounded"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => removeVendor(v)} data-testid={`vendor-delete-${v.id}`} title="Delete (Super Admin)" className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>
                  </td>
                )}
              </tr>
            ))}
            {vendors.length === 0 && <tr><td colSpan={isSuperAdmin ? 5 : 4} className="text-center py-10 text-[#8B7355]">No vendors yet — add your first FG/trading supplier.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayDialog({ purchase, onClose, onDone }) {
  const [amount, setAmount] = useState(purchase.balance_due || 0);
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/vendor-purchases/${purchase.id}/pay`, { amount: Number(amount), method, reference });
      toast.success("Payment recorded & posted to HO Ledger");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm bg-white" data-testid="vendor-pay-dialog">
        <DialogHeader><DialogTitle className="font-[Outfit]">Record Payment — {purchase.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-xs bg-[#F7F2E8] rounded p-2">Balance due: <span className="font-semibold">{inr(purchase.balance_due)}</span></div>
          <div><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="vendor-pay-amount" /></div>
          <div>
            <Label>Method</Label>
            <div className="flex gap-2 mt-1">
              {["cash", "bank", "upi"].map((m) => (
                <button key={m} onClick={() => setMethod(m)} data-testid={`vendor-pay-method-${m}`}
                  className={`flex-1 capitalize text-sm py-1.5 rounded border ${method === m ? "bg-[#C05621] text-white border-[#C05621]" : "border-[#EAE5D9] text-[#5C4033]"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div><Label>Transaction Reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="UTR / Cheque no. / Txn ID" data-testid="vendor-pay-reference" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="vendor-pay-submit" className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ purchase, onClose, onDone }) {
  const [qtys, setQtys] = useState({});
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const returned = {};
  (purchase.returns || []).forEach((r) => r.items.forEach((it) => { returned[it.product_id] = (returned[it.product_id] || 0) + it.quantity; }));
  const submit = async () => {
    const items = Object.entries(qtys).filter(([, q]) => Number(q) > 0).map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) }));
    if (!items.length) return toast.error("Enter a return quantity for at least one item");
    if (!reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      await api.post(`/vendor-purchases/${purchase.id}/return`, { items, reason });
      toast.success("Purchase Return / Debit Note recorded");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white" data-testid="vendor-return-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Purchase Return — {purchase.number}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Deducts HO Warehouse stock and issues a Debit Note reducing the vendor payable.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {purchase.items.map((it) => {
            const already = returned[it.product_id] || 0;
            const max = it.quantity - already;
            return (
              <div key={it.product_id} className="flex items-center justify-between gap-3 border border-[#EAE5D9] rounded p-2">
                <div className="text-sm"><div className="font-medium">{it.name}</div><div className="text-xs text-[#8B7355]">Purchased {it.quantity} {it.unit} · Returnable {max}</div></div>
                <Input type="number" min={0} max={max} className="w-24 h-8" value={qtys[it.product_id] || ""}
                  disabled={max <= 0}
                  onChange={(e) => setQtys({ ...qtys, [it.product_id]: e.target.value })}
                  data-testid={`vendor-return-qty-${it.product_id}`} />
              </div>
            );
          })}
          <div><Label>Reason *</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Damaged in transit, wrong item shipped" data-testid="vendor-return-reason" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="vendor-return-submit" className="bg-red-600 hover:bg-red-700 text-white">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Issue Debit Note"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PurchasesTab({ purchases, vendors, load }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPurchaseForm);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [paying, setPaying] = useState(null);
  const [returning, setReturning] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [savedPurchase, setSavedPurchase] = useState(null);
  const [showDispatch, setShowDispatch] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";
  const canDispatch = DISPATCH_ROLES.includes(user?.role_id || user?.role);

  const updateItem = (i, patch) => setForm((f) => {
    const items = [...f.items]; items[i] = { ...items[i], ...patch }; return { ...f, items };
  });
  const pickProduct = (i, p) => updateItem(i, { product_id: p.id, name: p.name, sku: p.sku, unit: p.unit, gst_rate: p.gst_rate ?? 5, ho_transfer_rate: p.ho_transfer_cost ?? "", barcode: p.barcode ?? "", label_ingredients: p.label_ingredients ?? "" });

  const totals = React.useMemo(() => {
    let sub = 0, gst = 0;
    form.items.forEach((it) => {
      const net = Number(it.quantity) * Number(it.unit_price);
      sub += net; gst += net * (Number(it.gst_rate || 0) / 100);
    });
    return { sub, gst, grand: sub + gst };
  }, [form.items]);

  const submit = async () => {
    if (!form.vendor_id) return toast.error("Select a vendor");
    if (!form.items.length || form.items.some((it) => !it.product_id)) return toast.error("Pick a product for every line item");
    if (form.items.some((it) => !it.mfg_date || !it.expiry_date)) return toast.error("Mfg Date & Expiry Date are required for every line item");
    setSaving(true);
    try {
      const payload = { ...form, items: form.items.map((it) => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price), gst_rate: Number(it.gst_rate), ho_transfer_rate: it.ho_transfer_rate === "" || it.ho_transfer_rate == null ? null : Number(it.ho_transfer_rate) })) };
      if (editingId) {
        const { data } = await api.patch(`/vendor-purchases/${editingId}`, payload);
        toast.success(`${data.number} updated`);
        setOpen(false); setForm(emptyPurchaseForm); setEditingId(null); load();
      } else {
        const { data } = await api.post("/vendor-purchases", payload);
        toast.success(`${data.number} recorded — HO Warehouse stock credited, ready to dispatch`);
        setForm(emptyPurchaseForm); load();
        if (canDispatch) setSavedPurchase(data); else setOpen(false);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openEdit = (p) => {
    setForm({
      vendor_id: p.vendor_id, is_interstate: p.is_interstate, notes: p.notes || "", invoice_ref: p.invoice_ref || "",
      doc_date: p.doc_date || (p.created_at || "").slice(0, 10) || todayStr(),
      items: p.items.map((it) => ({ ...it })),
    });
    setEditingId(p.id); setOpen(true);
  };

  const [deleting, setDeleting] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const deletePurchase = async (p) => {
    setDeletePreview(null);
    setDeleting(p);
    try {
      const { data } = await api.get(`/vendor-purchases/${p.id}/delete-preview`);
      setDeletePreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load delete preview"); }
  };
  const deleteEffects = (p) => {
    if (!p) return [];
    const pv = deletePreview;
    const livePays = (p.payments || []).filter((x) => !x.voided);
    const paid = pv ? pv.payments.amount : livePays.reduce((a, x) => a + Number(x.amount || 0), 0);
    const payCount = pv ? pv.payments.count : livePays.length;
    const retCount = pv ? pv.returns.count : (p.returns || []).length;
    const retAmt = pv ? pv.returns.amount : (p.returns || []).reduce((a, r) => a + Number(r.grand_total || 0), 0);
    const qty = pv ? pv.warehouse_qty : (p.items || []).reduce((a, it) => a + Number(it.quantity || 0), 0);
    const downstream = pv?.downstream_dispatches || [];
    const blockers = pv?.blockers || [];
    return [
      ...downstream.map((d) => `Reverse downstream dispatch ${d.number} (${d.status}) → ${d.to_branch_name}: ${d.reverse_from_showroom_qty > 0 ? `pull back ${d.reverse_from_showroom_qty} unit(s) from the showroom and void GRN ${(d.grn_numbers || []).join(", ")}, ` : ""}restore ${d.restore_to_source_qty} unit(s) to HO`),
      ...blockers.map((b) => `BLOCKED — ${b}`),
      payCount > 0 && `Reverse ${inr(paid)} in ${payCount} vendor payment(s) — mirrored ledger entries posted, payments voided, Paid Amount reset to ₹0`,
      retCount > 0 && `Reverse ${retCount} purchase return / debit note(s) worth ${inr(retAmt)}`,
      qty > 0 && `Remove ${qty} unit(s) from the HO Master Warehouse stock`,
      `Reverse the purchase ledger entry of ${inr(p.grand_total)} for ${p.vendor_snapshot?.name || "vendor"}`,
    ];
  };
  const [voiding, setVoiding] = useState(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const voidEffects = (p) => {
    if (!p) return [];
    const livePays = (p.payments || []).filter((x) => !x.voided);
    const paid = livePays.reduce((a, x) => a + Number(x.amount || 0), 0);
    return [
      "Mark the purchase VOIDED — it is excluded from the Vendor Ledger, balances and every report (the record stays visible for audit)",
      livePays.length > 0 && `Reverse ${inr(paid)} in ${livePays.length} vendor payment(s) — mirrored ledger entries, payments voided, Paid Amount reset to ₹0`,
      (p.returns || []).length > 0 && `Reverse ${(p.returns || []).length} purchase return / debit note(s)`,
      "Reverse HO Warehouse stock only as far as it still exists — if the stock is already 0 or moved on, nothing is blocked and the difference is logged",
      `Reverse the purchase ledger entry of ${inr(p.grand_total)}`,
    ];
  };
  const confirmVoid = async () => {
    if (!voiding) return;
    setVoidBusy(true);
    try {
      const { data } = await api.post(`/vendor-purchases/${voiding.id}/void`, { reason: "Legacy record voided by HO Super Admin" });
      const skipped = (data.stock_reversed || []).reduce((a, r) => a + Number(r.skipped || 0), 0);
      toast.success(`${voiding.number} voided${skipped ? ` — ${skipped} unit(s) were already gone, skipped` : ""}`);
      setVoiding(null); setViewing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to void"); }
    finally { setVoidBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/vendor-purchases/${deleting.id}`);
      const n = data?.payments_reversed?.count || 0;
      const dn = data?.dispatches_reversed?.length || 0;
      toast.success(`${deleting.number} deleted — ${dn ? `${dn} dispatch(es), ` : ""}${n ? `${n} payment(s), ` : ""}stock & ledger reversed`);
      setDeleting(null); setViewing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setDeleteBusy(false); }
  };

  const refreshViewing = async (id) => {
    const { data } = await api.get(`/vendor-purchases/${id}`);
    setViewing(data);
  };

  return (
    <div className="space-y-4">
      <ForceDeleteDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Force-delete Vendor Purchase"
        subject={deleting?.number} effects={deleteEffects(deleting)} onConfirm={confirmDelete} busy={deleteBusy} testid="vp-force-delete" />
      <ForceDeleteDialog open={!!voiding} onOpenChange={(o) => !o && setVoiding(null)} title="Void Vendor Purchase (legacy-safe)"
        subject={voiding?.number} effects={voidEffects(voiding)} onConfirm={confirmVoid} busy={voidBusy} testid="vp-void" verb="void" actionNoun="void" confirmLabel="Confirm Void" busyLabel="Voiding…" />
      <div className="flex justify-end gap-2">
        <BulkImportDialog onDone={load} />
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyPurchaseForm); setEditingId(null); setSavedPurchase(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="vp-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New Purchase
            </Button>
          </DialogTrigger>
          {savedPurchase ? (
            <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="vp-saved-dialog">
              <DialogHeader>
                <DialogTitle className="font-[Outfit] flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> {savedPurchase.number} saved</DialogTitle>
                <DialogDescription className="text-xs text-[#8B7355]">{savedPurchase.vendor_snapshot?.name} · {savedPurchase.items?.length} line{savedPurchase.items?.length === 1 ? "" : "s"} · {inr(savedPurchase.grand_total)} · stock credited at {savedPurchase.to_branch_name}. Optionally send some of it on to showrooms now.</DialogDescription>
              </DialogHeader>
              <VpDispatchSection purchase={savedPurchase} onDone={load} />
              <DialogFooter>
                <Button onClick={() => { setOpen(false); setSavedPurchase(null); }} data-testid="vp-saved-done-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Done</Button>
              </DialogFooter>
            </DialogContent>
          ) : (
          <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-[Outfit]">{editingId ? "Edit Vendor Purchase" : "New Vendor Purchase"}</DialogTitle>
              <DialogDescription className="text-xs text-[#8B7355]">Instantly credits HO Master Warehouse stock and books the vendor liability.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Vendor *</Label>
                  <select className="w-full h-9 border border-[#EAE5D9] rounded-md px-2 text-sm" value={form.vendor_id}
                    onChange={(e) => setForm({ ...form, vendor_id: e.target.value })} data-testid="vp-vendor-select">
                    <option value="">Select vendor…</option>
                    {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </div>
                <div><Label>Invoice / Bill Ref</Label><Input value={form.invoice_ref} onChange={(e) => setForm({ ...form, invoice_ref: e.target.value })} data-testid="vp-invoice-ref" /></div>
                <div>
                  <Label>Purchase Date *</Label>
                  <Input type="date" value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} data-testid="vp-doc-date" />
                  <p className="text-[10px] text-[#8B7355] mt-1">Defaults to today — back-date or post-date freely. Ledger & reports use this date.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_interstate} onCheckedChange={(v) => setForm({ ...form, is_interstate: v })} data-testid="vp-interstate-switch" />
                <Label>Inter-state (IGST)</Label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="uc-label">Line Items</div>
                  <div className="flex items-center gap-2">
                    <QuickAddProduct label="Product" onAdded={(p) => {
                      const idx = form.items.findIndex((it) => !it.product_id);
                      if (idx >= 0) { pickProduct(idx, p); }
                      else { setForm((f) => ({ ...f, items: [...f.items, { ...emptyItem, product_id: p.id, name: p.name, sku: p.sku, unit: p.unit, gst_rate: p.gst_rate ?? 5, ho_transfer_rate: p.ho_transfer_cost ?? "" }] })); }
                    }} />
                    <Button size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })}><Plus className="w-3 h-3 mr-1" /> Add Row</Button>
                  </div>
                </div>
                <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F2E8] uc-label">
                      <tr>
                        <th className="text-left px-2 py-2 w-56">Product</th>
                        <th className="text-right px-2 py-2 w-20">Qty</th>
                        <th className="text-left px-2 py-2 w-16">Unit</th>
                        <th className="text-right px-2 py-2 w-24">Price</th>
                        <th className="text-right px-2 py-2 w-16">GST</th>
                        <th className="text-right px-2 py-2 w-24">Total</th>
                        <th className="text-right px-2 py-2 w-28 text-[#C05621] whitespace-nowrap" title="Rate at which HO supplies this item to showrooms">HO Rate ₹</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, i) => {
                        const net = Number(it.quantity) * Number(it.unit_price);
                        const gst = net * (it.gst_rate / 100);
                        return (
                          <React.Fragment key={i}>
                            <tr className="border-t border-[#EAE5D9]">
                              <td className="px-2 py-1.5">
                                {it.product_id ? (
                                  <div className="text-xs flex items-center justify-between gap-1">
                                    <span className="font-medium">{it.name} <span className="text-[#8B7355]">({it.sku})</span></span>
                                    <button onClick={() => updateItem(i, { ...emptyItem })} className="text-[10px] text-[#C05621] underline">change</button>
                                  </div>
                                ) : (
                                  <ProductQuickPicker testid={`vp-product-picker-${i}`} onPick={(p) => pickProduct(i, p)} />
                                )}
                              </td>
                              <td className="px-2 py-1.5"><Input type="number" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} className="h-8 text-right" data-testid={`vp-qty-${i}`} /></td>
                              <td className="px-2 py-1.5"><Input value={it.unit} onChange={(e) => updateItem(i, { unit: e.target.value })} className="h-8" /></td>
                              <td className="px-2 py-1.5"><Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} className="h-8 text-right" data-testid={`vp-price-${i}`} /></td>
                              <td className="px-2 py-1.5"><Input type="number" value={it.gst_rate} onChange={(e) => updateItem(i, { gst_rate: e.target.value })} className="h-8 text-right" /></td>
                              <td className="px-2 py-1.5 text-right font-medium">{inr(net + gst)}</td>
                              <td className="px-2 py-1.5"><Input type="number" min="0" step="0.01" value={it.ho_transfer_rate ?? ""} onChange={(e) => updateItem(i, { ho_transfer_rate: e.target.value })} className="h-8 text-right border-[#C05621]/40" placeholder="Supply ₹" data-testid={`vp-ho-rate-${i}`} /></td>
                              <td className="px-2 py-1.5">
                                {form.items.length > 1 && <button onClick={() => setForm({ ...form, items: form.items.filter((_, x) => x !== i) })} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                              </td>
                            </tr>
                            <tr className="border-t border-dashed border-[#EAE5D9] bg-[#FDFBF7]">
                              <td colSpan={8} className="px-2 py-1.5">
                                <div className="flex items-center gap-3 text-xs">
                                  <span className="text-[#8B7355] whitespace-nowrap">Batch dates *</span>
                                  <Input type="date" value={it.mfg_date} onChange={(e) => updateItem(i, { mfg_date: e.target.value })} className="h-7 text-xs" placeholder="Mfg Date" data-testid={`vp-mfg-date-${i}`} />
                                  <Input type="date" value={it.expiry_date} onChange={(e) => updateItem(i, { expiry_date: e.target.value })} className="h-7 text-xs" placeholder="Expiry Date" data-testid={`vp-expiry-date-${i}`} />
                                  <span className="text-[#8B7355] whitespace-nowrap ml-2">Barcode</span>
                                  <Input value={it.barcode || ""} onChange={(e) => updateItem(i, { barcode: e.target.value })} className="h-7 text-xs w-40 font-mono" placeholder="EAN / code" data-testid={`vp-barcode-${i}`} />
                                  <span className="text-[#8B7355] whitespace-nowrap">Label ingredients</span>
                                  <Input value={it.label_ingredients || ""} onChange={(e) => updateItem(i, { label_ingredients: e.target.value })} className="h-7 text-xs flex-1" placeholder="Wheat flour, sugar, butter…" data-testid={`vp-label-ingredients-${i}`} />
                                </div>
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="bg-[#F7F2E8] rounded-md p-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>{inr(totals.sub)}</span></div>
                  <div className="flex justify-between"><span>GST ({form.is_interstate ? "IGST" : "CGST+SGST"})</span><span>{inr(totals.gst)}</span></div>
                  <div className="flex justify-between pt-2 border-t border-[#EAE5D9] font-semibold"><span>Grand Total</span><span className="text-[#C05621]">{inr(totals.grand)}</span></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="vp-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Save Changes" : "Save Purchase (credits HO Warehouse)"}
              </Button>
            </DialogFooter>
          </DialogContent>
          )}
        </Dialog>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Number</th><th className="px-4 py-3">Vendor</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Grand Total</th>
              <th className="px-4 py-3 text-right">Balance Due</th><th className="px-4 py-3">Date</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((p) => {
              const isVoided = p.status === "voided";
              return (
              <tr key={p.id} className={`border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7] ${isVoided ? "opacity-60 line-through decoration-stone-400" : ""}`} data-testid={`vp-row-${p.id}`}>
                <td className="px-4 py-3 font-medium">{p.number}</td>
                <td className="px-4 py-3">{p.vendor_snapshot?.name}</td>
                <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${isVoided ? "bg-stone-200 text-stone-700 border-stone-300" : statusColor(p.status)}`} data-testid={`vp-status-${p.id}`}>{p.status}</Badge></td>
                <td className="px-4 py-3 text-right font-semibold">{inr(p.grand_total)}</td>
                <td className="px-4 py-3 text-right">{p.balance_due > 0 ? <span className="text-red-600 font-medium">{inr(p.balance_due)}</span> : <span className="text-emerald-600">₹0</span>}</td>
                <td className="px-4 py-3 text-[#8B7355]" data-testid={`vp-row-date-${p.id}`}>{fmtDate(p.doc_date || p.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => refreshViewing(p.id)} className="p-1.5 hover:bg-[#F7F2E8] rounded" data-testid={`vp-view-${p.id}`}><Eye className="w-4 h-4" /></button>
                  {!isVoided && p.balance_due > 0 && <button onClick={() => setPaying(p)} className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded" title="Record payment" data-testid={`vp-pay-${p.id}`}><Wallet className="w-4 h-4" /></button>}
                  {!isVoided && <button onClick={() => setReturning(p)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" title="Purchase return" data-testid={`vp-return-${p.id}`}><Undo2 className="w-4 h-4" /></button>}
                  {isSuperAdmin && (
                    <>
                      {!isVoided && <button onClick={() => openEdit(p)} title="Edit (Super Admin — payments/returns are auto-reversed)"
                        className="p-1.5 hover:bg-blue-50 text-blue-600 rounded" data-testid={`vp-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>}
                      {!isVoided && <button onClick={() => setVoiding(p)} title="Void (Super Admin — legacy-safe, never blocks on stock)"
                        className="p-1.5 hover:bg-stone-100 text-stone-700 rounded" data-testid={`vp-void-${p.id}`}><Ban className="w-4 h-4" /></button>}
                      <button onClick={() => deletePurchase(p)} title="Force-delete (Super Admin)"
                        className="p-1.5 hover:bg-red-50 text-red-600 rounded" data-testid={`vp-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </td>
              </tr>
              );
            })}
            {purchases.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]">No vendor purchases yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); setShowDispatch(false); } }}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <DialogTitle className="font-[Outfit] text-2xl">{viewing.number}</DialogTitle>
                  <div className="flex items-center gap-2">
                    {canDispatch && viewing.status !== "voided" && (
                      <Button size="sm" variant="outline" onClick={() => setShowDispatch((v) => !v)} className="border-[#C05621] text-[#C05621] hover:bg-[#FDF3EC]" data-testid="vp-view-dispatch-toggle">
                        <Truck className="w-4 h-4 mr-1" /> {showDispatch ? "Hide dispatch" : "Dispatch to Showroom"}
                      </Button>
                    )}
                    <Badge variant="outline" className={`capitalize ${statusColor(viewing.status)}`}>{viewing.status}</Badge>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                {showDispatch && <VpDispatchSection purchase={viewing} onDone={() => { load(); refreshViewing(viewing.id); }} />}
                {!showDispatch && (viewing.direct_dispatches || []).length > 0 && (
                  <div className="text-xs text-[#5C4033] bg-[#FBF8F2] border border-[#EAE5D9] rounded px-3 py-2" data-testid="vp-view-direct-dispatches">
                    <span className="uc-label mr-2">Sent to showrooms</span>
                    {viewing.direct_dispatches.map((d) => <span key={d.dispatch_id} className="mr-3">{d.to_branch_name} · <span className="font-mono">{d.dispatch_number}</span> / <span className="font-mono">{d.grn_number}</span></span>)}
                  </div>
                )}
                <div className="text-sm"><span className="font-semibold">{viewing.vendor_snapshot?.name}</span> · Landed at {viewing.to_branch_name} · Purchase Date: <span className="font-medium" data-testid="vp-view-date">{fmtDate(viewing.doc_date || viewing.created_at)}</span>{viewing.invoice_ref ? ` · Bill Ref: ${viewing.invoice_ref}` : ""}</div>
                <div className="overflow-x-auto max-w-full"><table className="w-full text-sm border border-[#EAE5D9] rounded-md">
                  <thead className="bg-[#F7F2E8] uc-label"><tr><th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Price</th><th className="text-right px-3 py-2">Total</th><th className="text-right px-3 py-2 text-[#C05621]">HO Transfer Rate</th></tr></thead>
                  <tbody>
                    {viewing.items.map((it, i) => (
                      <tr key={i} className="border-t border-[#EAE5D9]"><td className="px-3 py-2">{it.name}{(it.barcode || it.label_ingredients) && <div className="text-[11px] text-[#8B7355] mt-0.5" data-testid={`vp-view-label-${i}`}>{it.barcode ? <span className="font-mono">Barcode {it.barcode}</span> : null}{it.barcode && it.label_ingredients ? " · " : ""}{it.label_ingredients ? <span>Ingredients: {it.label_ingredients}</span> : null}</div>}</td><td className="px-3 py-2 text-right">{it.quantity} {it.unit}</td><td className="px-3 py-2 text-right">{inr(it.unit_price)}</td><td className="px-3 py-2 text-right font-medium">{inr(it.quantity * it.unit_price * (1 + it.gst_rate / 100))}</td><td className="px-3 py-2 text-right text-[#C05621]" data-testid={`vp-view-ho-rate-${i}`}>{it.ho_transfer_rate ? inr(it.ho_transfer_rate) : <span className="text-[#8B7355] text-xs">not set</span>}</td></tr>
                    ))}
                  </tbody>
                </table></div>
                <div className="flex justify-end"><div className="w-64 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Grand Total</span><span>{inr(viewing.grand_total)}</span></div>
                  {viewing.returned_amount > 0 && <div className="flex justify-between text-red-600"><span>Returned</span><span>-{inr(viewing.returned_amount)}</span></div>}
                  <div className="flex justify-between"><span>Paid</span><span>{inr(viewing.paid_amount)}</span></div>
                  <div className="flex justify-between border-t border-[#EAE5D9] pt-1.5 font-semibold text-lg text-[#C05621]"><span>Balance Due</span><span>{inr(viewing.balance_due)}</span></div>
                </div></div>
                {(viewing.payments?.length > 0) && (
                  <div><div className="uc-label mb-1">Payment History</div>
                    {viewing.payments.map((p) => <div key={p.id} className="text-xs flex justify-between border-b border-[#EAE5D9] py-1"><span className="capitalize">{p.method} · {p.reference || "—"}</span><span>{inr(p.amount)} · {fmtDate(p.paid_at)}</span></div>)}
                  </div>
                )}
                {(viewing.returns?.length > 0) && (
                  <div><div className="uc-label mb-1">Returns / Debit Notes</div>
                    {viewing.returns.map((r) => <div key={r.id} className="text-xs flex justify-between border-b border-[#EAE5D9] py-1"><span>{r.number} · {r.reason}</span><span>{inr(r.grand_total)} · {fmtDate(r.created_at)}</span></div>)}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {paying && <PayDialog purchase={paying} onClose={() => setPaying(null)} onDone={() => { setPaying(null); load(); }} />}
      {returning && <ReturnDialog purchase={returning} onClose={() => setReturning(null)} onDone={() => { setReturning(null); load(); }} />}
    </div>
  );
}

export default function VendorPurchasesPage() {
  const { user: pageUser } = useAuth();
  const [tab, setTab] = useState("purchases");
  const [vendors, setVendors] = useState([]);
  const [purchases, setPurchases] = useState([]);

  const load = async () => {
    const [v, p] = await Promise.all([api.get("/vendors"), api.get("/vendor-purchases")]);
    setVendors(v.data); setPurchases(p.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="vendor-purchases-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Third-Party Trading</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <Truck className="w-7 h-7 text-[#C05621]" /> Vendor Purchases
          </h1>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent flex-wrap h-auto p-0 gap-1">
          <ColorTab value="purchases" tone="orange" testid="vp-tab-purchases">Purchases</ColorTab>
          <ColorTab value="vendors" tone="slate" testid="vp-tab-vendors">Vendors</ColorTab>
        </TabsList>
        <TabsContent value="purchases" className="pt-4">
          <PurchasesTab purchases={purchases} vendors={vendors} load={load} />
        </TabsContent>
        <TabsContent value="vendors" className="pt-4">
          <VendorsTab vendors={vendors} load={load} isSuperAdmin={(pageUser?.role_id || pageUser?.role) === "super_admin"} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
